import { Events } from '@/services/Events';
import { onFabricCanvasScaleCallback } from '@/infrastructure/engine2d/FabricCallbacks';
import {
    onMouseWheelContextualMenu,
    onMoveContextualMenu,
    onSelectContextualMenu,
    onTextChangeContextualMenu,
} from '@/infrastructure/engine2d/events/contextualMenuCallbacks';
import type { BasicTransformEvent, Canvas, FabricObject } from 'fabric';

// What fabric gives a textbox: it can be opened for typing, and its whole content selected.
type TEditableText = FabricObject & {
    enterEditing?: () => void;
    getSelectionStartFromPointer?: (event: Event) => number;
    id?: string;
    isEditing?: boolean;
    selectAll?: () => void;
    setSelectionEnd?: (index: number) => void;
    setSelectionStart?: (index: number) => void;
};

// A canvas has none of the phone's text gestures: there is no caret to drag and no long press
// to select with. Both are rebuilt here on the text open for typing.
const LONG_PRESS_MS = 450;
const LONG_PRESS_MOVE_TOLERANCE_PX = 8;

type TObjectModifiedCallback = (event: BasicTransformEvent & { target: FabricObject }) => void;

type TFabricSceneEventsServiceParams = {
    canvas: Canvas;
    objectMovingConstrain: TObjectModifiedCallback;
};

// Owns the on/off lifecycle for scene-level fabric events that don't
// belong to any specific overlay: object constraint on move + the
// contextual-menu bookkeeping. Overlay-specific listeners (mask
// opacity on selection) live in FabricPrintZoneEngine2D. Pointer input
// (pan / zoom / touch) lives in input/{Pan,Zoom,Touch}Controller.
class FabricSceneEventsService {
    private readonly _canvas: Canvas;
    private readonly _objectMovingConstrain: TObjectModifiedCallback;
    private _isSelectingText = false;
    private _longPressTimer: ReturnType<typeof setTimeout> | undefined;
    private _pressOrigin: { x: number; y: number } | undefined;
    private _selectionAnchor = 0;

    constructor(params: TFabricSceneEventsServiceParams) {
        this._canvas = params.canvas;
        this._objectMovingConstrain = params.objectMovingConstrain;
        this._attach();
    }

    dispose(): void {
        this._cancelLongPress();
        this._canvas.off('mouse:down', this._onPointerDown);
        this._canvas.off('mouse:move', this._onPointerMove);
        this._canvas.off('mouse:up', this._onPointerUp);
        this._canvas.off('text:editing:entered', this._onTextEditingEntered);
        this._canvas.off('text:editing:exited', this._onTextEditingExited);
        this._canvas.off('mouse:dblclick', this._openTextForTyping);
        this._canvas.off('object:moving', this._objectMovingConstrain);

        this._canvas.off('object:moving', onMoveContextualMenu);
        this._canvas.off('object:scaling', onMoveContextualMenu);
        this._canvas.off('object:rotating', onMoveContextualMenu);

        this._canvas.off('selection:created', onSelectContextualMenu);
        this._canvas.off('selection:updated', onSelectContextualMenu);
        this._canvas.off('text:changed', onTextChangeContextualMenu);
        this._canvas.off('object:scaling', onFabricCanvasScaleCallback);
        this._canvas.off('mouse:wheel', onMouseWheelContextualMenu);
    }

    // A double tap on a text opens it for typing with the whole content selected, so the
    // next keystroke replaces it — on a phone, deleting a placeholder letter by letter with
    // a caret you can barely aim is the fastest way to give up. Fabric's own double click
    // only takes the word under the finger. Opening the editor also focuses its hidden
    // input, which is what brings the keyboard up.
    private readonly _openTextForTyping = (event: { target?: FabricObject }): void => {
        const target = event.target as TEditableText | undefined;
        if (!target || typeof target.enterEditing !== 'function' || typeof target.selectAll !== 'function') {
            return;
        }
        if (!target.isEditing) {
            target.enterEditing();
        }
        // After fabric's own double-click handling, which takes the word under the finger.
        requestAnimationFrame(() => {
            target.selectAll?.();
            this._canvas.requestRenderAll();
        });
    };

    private readonly _onTextEditingEntered = (event: { target?: FabricObject }): void => {
        Events.emit('text-editing', (event.target as TEditableText | undefined)?.id);
    };

    private readonly _onTextEditingExited = (): void => {
        Events.emit('text-editing', undefined);
    };

    // Dragging inside the text being typed on moves the selection from where the finger
    // landed, and holding still selects the whole of it — the two gestures a phone gives for
    // free in a native field, and neither of which a canvas has.
    private readonly _onPointerDown = (event: { e: Event; scenePoint?: unknown }): void => {
        const target = this._textBeingTyped();
        if (!target?.getSelectionStartFromPointer) {
            return;
        }
        this._selectionAnchor = target.getSelectionStartFromPointer(event.e);
        this._isSelectingText = true;
        this._pressOrigin = this._pointerPosition(event.e);
        this._longPressTimer = globalThis.setTimeout(() => {
            this._isSelectingText = false;
            target.selectAll?.();
            this._canvas.requestRenderAll();
        }, LONG_PRESS_MS);
    };

    private readonly _onPointerMove = (event: { e: Event }): void => {
        if (!this._isSelectingText) {
            return;
        }
        const target = this._textBeingTyped();
        if (!target?.getSelectionStartFromPointer) {
            return;
        }
        const moved = this._pointerPosition(event.e);
        if (
            this._pressOrigin &&
            (Math.abs(moved.x - this._pressOrigin.x) > LONG_PRESS_MOVE_TOLERANCE_PX ||
                Math.abs(moved.y - this._pressOrigin.y) > LONG_PRESS_MOVE_TOLERANCE_PX)
        ) {
            this._cancelLongPress();
        }
        const index = target.getSelectionStartFromPointer(event.e);
        target.setSelectionStart?.(Math.min(this._selectionAnchor, index));
        target.setSelectionEnd?.(Math.max(this._selectionAnchor, index));
        this._canvas.requestRenderAll();
    };

    private readonly _onPointerUp = (): void => {
        this._isSelectingText = false;
        this._cancelLongPress();
    };

    private _textBeingTyped(): TEditableText | undefined {
        const active = this._canvas.getActiveObject() as TEditableText | undefined;
        return active?.isEditing ? active : undefined;
    }

    private _pointerPosition(event: Event): { x: number; y: number } {
        const touch = (event as TouchEvent).touches?.[0];
        if (touch) {
            return { x: touch.clientX, y: touch.clientY };
        }
        const pointer = event as MouseEvent;
        return { x: pointer.clientX ?? 0, y: pointer.clientY ?? 0 };
    }

    private _cancelLongPress(): void {
        if (this._longPressTimer !== undefined) {
            globalThis.clearTimeout(this._longPressTimer);
            this._longPressTimer = undefined;
        }
    }

    private _attach(): void {
        this._canvas.on('mouse:down', this._onPointerDown);
        this._canvas.on('mouse:move', this._onPointerMove);
        this._canvas.on('mouse:up', this._onPointerUp);
        this._canvas.on('text:editing:entered', this._onTextEditingEntered);
        this._canvas.on('text:editing:exited', this._onTextEditingExited);
        this._canvas.on('mouse:dblclick', this._openTextForTyping);
        this._canvas.on('object:moving', this._objectMovingConstrain);

        this._canvas.on('object:moving', onMoveContextualMenu);
        this._canvas.on('object:scaling', onMoveContextualMenu);
        this._canvas.on('object:rotating', onMoveContextualMenu);

        this._canvas.on('selection:created', onSelectContextualMenu);
        this._canvas.on('selection:updated', onSelectContextualMenu);

        this._canvas.on('text:changed', onTextChangeContextualMenu);
        this._canvas.on('object:scaling', onFabricCanvasScaleCallback);
        this._canvas.on('mouse:wheel', onMouseWheelContextualMenu);
    }
}

export { FabricSceneEventsService };
